import { Card, CardContent, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import {
  Datagrid,
  FunctionField,
  TextField,
  useListContext,
  useStore,
  useTranslate,
} from 'react-admin'
import { useLocation } from 'react-router-dom'
import { GlucoseLineChart } from '../metrics/GlucoseLineChart'
import { formatTimezoneForDisplay } from '../utils/timezoneUtils'

interface DeviceDatagridProps {
  rowClick?: string | ((id: any, resource: any, record: any) => any)
}

const DeviceDatagrid = ({ rowClick = 'edit' }: DeviceDatagridProps) => {
  const translate = useTranslate()
  const [selectedDeviceId, setSelectedDeviceId] = useStore('selectedDeviceId')
  const [selectedPetId, setSelectedPetId] = useStore('selectedPetId')
  const [isSelectionActive, setIsSelectionActive] = useState(false)
  const [filterName, setFilterName] = useState('')
  // One-time flag to pick default on initial load
  const hasInitializedDefault = useRef(false)
  const location = useLocation()
  const { data, isLoading } = useListContext()

  const filteredData = !data
    ? []
    : data.filter((record: any) => record.name?.toLowerCase().includes(filterName.toLowerCase()))

  useEffect(() => {
    if (!isLoading && data?.length && !hasInitializedDefault.current) {
      setSelectedDeviceId(data[0].id)
      // Mirror row-click: select the owning pet too, so the glucose chart
      // below resolves THIS pet's target band on first load instead of a
      // stale prior selection's (or the dog fallback for a cat).
      const petId = typeof data[0].pet === 'object' ? data[0].pet?.id : data[0].pet
      if (petId) {
        setSelectedPetId(petId)
      }
      hasInitializedDefault.current = true
    }
  }, [isLoading, data, setSelectedDeviceId, setSelectedPetId])

  const handleRowClick =
    typeof rowClick === 'function'
      ? rowClick
      : (id: any, resource: any, record: any) => {
          // Handle "select" mode: only update selection, no navigation
          if (rowClick === 'select' || rowClick === '') {
            setSelectedDeviceId(record.id)
            // Also set the pet that owns this device
            const petId = typeof record.pet === 'object' ? record.pet.id : record.pet
            if (petId) {
              setSelectedPetId(petId)
            }
            return false // prevent navigation
          }
          // For other string modes (e.g., 'edit'), navigate to that view
          if (typeof rowClick === 'string' && rowClick) {
            return rowClick
          }
          // Fallback: no action
          return false
        }

  return (
    <div>
      <Datagrid
        data={filteredData} // Pass filtered data
        rowClick={handleRowClick}
        bulkActionButtons={false}
        rowStyle={(record) =>
          record.id === selectedDeviceId ? { backgroundColor: 'rgba(79, 60, 201, 0.08)' } : {}
        }
      >
        <TextField source="id" />
        <TextField source="name" />
        <FunctionField
          label={translate('resources.devices.fields.pet')}
          render={(record) => {
            if (typeof record.pet === 'object') {
              return record.pet.name || `Pet ID: ${record.pet.id}`
            }
            return record.pet
          }}
        />
        <FunctionField
          source="start_time"
          label={translate('resources.devices.fields.start_time')}
          render={(record) =>
            record.start_time
              ? new Date(record.start_time * 1000).toLocaleString()
              : translate('pos.common.empty_data')
          }
        />
        <FunctionField
          source="timezone"
          label={translate('resources.devices.fields.timezone')}
          render={(record) => {
            if (!record.timezone && record.timezone !== 0) {
              return translate('pos.common.empty_data')
            }

            const formatted = formatTimezoneForDisplay(record.timezone)
            return formatted || translate('pos.common.empty_data')
          }}
        />
      </Datagrid>
      {selectedDeviceId && location.pathname.startsWith('/devices') && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {translate('resources.devices.fields.glucose_trend')}
            </Typography>
            <GlucoseLineChart enableRangeSlider={true} />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default DeviceDatagrid
